import { useState, useEffect, useMemo, useCallback } from 'react';
import { 
    collectionGroup, 
    query, 
    onSnapshot, 
    doc,
    getDoc,
    orderBy,
    deleteDoc
} from 'firebase/firestore';

import { db } from '../../../../../firebase/config';
import { useAuth } from '../../../../hooks/useAuth'; 
import { deleteCotizacionArtFull } from '../../../../../firebase/firebaseUtils';
import { useTableState } from '../../hooks/useTableState';

export const useCotizacionesArtTable = () => {
    const { user, profile } = useAuth();
    
    const isFullUser = true;

    // --- ESTADOS DE CONTROL DE VISTA ---
    const [view, setView] = useState('list');
    const [step, setStep] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    // --- ESTADOS DE BASE DE DATOS ---
    const [realTimeCotizaciones, setRealTimeCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE BUSCADOR DE ENTIDADES EN CREACIÓN ---
    const [searchResults, setSearchResults] = useState([]);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);

    // --- ENTIDADES SELECCIONADAS ---
    const [selectedClient, setSelectedClient] = useState(null);
    const [isProspect, setIsProspect] = useState(false);
    const [editingCotizacion, setEditingCotizacion] = useState(null);
    const [detailCotizacion, setDetailCotizacion] = useState(null);
    const [showProspectModal, setShowProspectModal] = useState(false);
    const [editingProspectData, setEditingProspectData] = useState(null);
    const [authModalConfig, setAuthModalConfig] = useState({ open: false, type: '', item: null });

    const limiteFecha = useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 6);
        return date.toISOString().split('T')[0];
    }, []);

    // 1. ESCUCHADOR EN TIEMPO REAL FIRESTORE
    useEffect(() => {
        if (!db) return;
        
        const q = query(collectionGroup(db, 'cotizaciones_art'), orderBy('fechaCarga', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => {
                const docData = d.data();
                const pathParts = d.ref.path.split('/');
                const cliId = pathParts[3] || docData.clienteId || docData.entidadId;

                return {
                    id: d.id,
                    path: d.ref.path,
                    ...docData,
                    clienteId: cliId,
                    clienteNombre: docData.clienteNombre || docData.entidadNombre || '',
                    clienteCuit: docData.clienteCuit || docData.entidadCuit || '',
                    canal: docData.canal || docData.tipoCanal || docData.canalOrigen || '-',
                    canalNombre: docData.canalNombre || docData.nombreCanal || docData.canalNombreId || '-',
                    subcanalNombre: docData.subcanalNombre || docData.subcanalAsociado || docData.subcanalNombreId || '-'
                };
            });
            setRealTimeCotizaciones(data);
            setLoading(false);
        }, (error) => {
            console.error("Error en onSnapshot Cotizaciones ART:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // 2. ARREGLO DE DATOS MEMOIZADO ESTABLE CON HIGIENE DE NOMBRES Y APELIDOS
    const itemsTable = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase().trim();
        
        const filteredByTime = realTimeCotizaciones.filter(item => {
            if (lowerSearch.length >= 3) return true;
            return item.fechaCarga >= limiteFecha;
        });

        return filteredByTime.map(item => {
            const nombre = (item.clienteNombre || item.entidadNombre || '').trim();
            const apellido = (item.clienteApellido || '').trim();

            // Saneamiento: Si el apellido es 'S/D', 'SD' o está vacío, no se concatena (ideal para personas jurídicas)
            const esValidoApellido = apellido && !['S/D', 'SD', 'S/A', 'SIN DATOS'].includes(apellido.toUpperCase());
            const fullName = esValidoApellido ? `${nombre} ${apellido}`.trim() : nombre;

            const cotizacionesArray = Array.isArray(item.cotizaciones) ? item.cotizaciones : [];
            const tieneSolicitada = (String(item.estado || '').toLowerCase().includes('solicit')) ||
                cotizacionesArray.some(c => {
                    const est = String(c.estadoCotizacion || '').trim().toLowerCase();
                    return est === 'solicitada' || est.includes('solicit') || est.includes('espera');
                });

            return {
                ...item,
                displayNombre: (fullName || 'SIN NOMBRE').toUpperCase(),
                displayDoc: item.clienteCuit || item.clienteDni || '-',
                importeDeuda: item.masaSalarial || 0,
                cotizacionPendiente: tieneSolicitada ? 'SI' : 'NO'
            };
        });
    }, [realTimeCotizaciones, searchTerm, limiteFecha]);

    // 3. ESTADO SÍNCRONO DE LA TABLA
    const rawTableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaCarga', direction: 'descending' },
        initialColumns: {
            fechaCarga: true, displayNombre: true, displayDoc: true, numeroCotizacion: true,
            tipoEntidad: true, tipoGestion: true, estado: true, canal: true,
            canalNombre: true, subcanalNombre: true, vendedor: true, responsableAsignado: true
        },
        initialFilters: {
            estado: ['Pendiente', 'Solicitud de Cotización', 'Cotizando', 'Esperando Respuesta', 'Seguimiento'] // Sello de filtro por defecto al ingresar
        },
        recordsPerPage: 100
    });

    // 4. INTERCEPTOR ESTABLE CON PRESERVACIÓN DE PÁGINA
    const baseHandleFilterChange = rawTableState.handleFilterChange;
    const handleFilterChange = useCallback((vals) => {
        const newSearch = vals?.search || '';
        setSearchTerm(prev => (prev !== newSearch ? newSearch : prev));
        baseHandleFilterChange(vals);
    }, [baseHandleFilterChange]);

    // 5. UNIFICACIÓN ESTABLE DE TABLESTATE
    const tableState = useMemo(() => ({
        ...rawTableState,
        handleFilterChange
    }), [rawTableState, handleFilterChange]);

    const handleCreateNew = useCallback(() => { 
        setView('create'); 
        setStep(1); 
        setEditingCotizacion(null); 
        setSelectedClient(null); 
        setSearchResults([]); 
        setSearchPerformed(false); 
    }, []);

    const handleEdit = useCallback(async (cot) => {
        setEditingCotizacion(cot);
        const colPath = cot.tipoEntidad === 'PROSPECTO' ? 'prospectos' : 'clientes';
        const cliSnap = await getDoc(doc(db, `public/data/${colPath}`, cot.clienteId));
        if (cliSnap.exists()) {
            setSelectedClient({ id: cliSnap.id, ...cliSnap.data() });
        } else {
            setSelectedClient({ id: cot.clienteId, nombre: cot.clienteNombre, apellido: cot.clienteApellido, cuit: cot.clienteCuit });
        }
        setIsProspect(cot.tipoEntidad === 'PROSPECTO');
        setView('edit');
        setStep(2);
    }, []);

    const handleViewDetail = useCallback((cot, navigate) => {
        if (cot.tipoGestion === 'Aumento de Tarifa') {
            navigate('/DetalleAumentoArt', { 
                state: { 
                    cotizacionId: cot.id, 
                    clienteId: cot.clienteId, 
                    entidadId: cot.clienteId, 
                    clientId: cot.clienteId, 
                    nCotizacion: cot.numeroCotizacion, 
                    cotizacion: cot,
                    contratoId: cot.contratoOrigenId || cot.contratoVinculadoId,
                    endosoId: cot.endosoVinculadoId,
                    fromTableroAumentos: true 
                } 
            });
        } else {
            setDetailCotizacion(cot);
            setView('detail');
        }
    }, []);

    const handleAction = useCallback((item, type, navigate) => {
        if (type === 'VIEW') handleViewDetail(item, navigate);
        if (type === 'EDIT') handleEdit(item);
        if (type === 'PROSPECT_EDIT') { 
            const fetchAndOpen = async () => {
                const snap = await getDoc(doc(db, 'public/data/prospectos', item.clienteId));
                if (snap.exists()) {
                    setEditingProspectData({ id: snap.id, ...snap.data() });
                    setShowProspectModal(true);
                }
            };
            fetchAndOpen();
        }
        if (type === 'DELETE') setAuthModalConfig({ open: true, item });
    }, [handleViewDetail, handleEdit]);

    const confirmDelete = async () => {
        const { item } = authModalConfig;
        if (!item) return;
        try { 
            if (item.tipoGestion === 'Aumento de Tarifa') {
                await deleteCotizacionArtFull(item); 
            } else {
                await deleteDoc(doc(db, item.path)); 
            }
            setAuthModalConfig({ open: false, type: '', item: null });
        } catch (error) { 
            console.error("Error al procesar eliminación de cotización:", error); 
        }
    };

    const handleSuccess = useCallback(() => {
        setView('list');
        setStep(1);
        setEditingCotizacion(null);
        setSelectedClient(null);
    }, []);

    return {
        view,
        setView,
        step,
        setStep,
        loading,
        searchResults,
        setSearchResults,
        searchPerformed,
        setSearchPerformed,
        loadingSearch,
        setLoadingSearch,
        selectedClient,
        setSelectedClient,
        isProspect,
        setIsProspect,
        editingCotizacion,
        detailCotizacion,
        setDetailCotizacion,
        showProspectModal,
        setShowProspectModal,
        editingProspectData,
        setEditingProspectData,
        authModalConfig,
        setAuthModalConfig,
        isFullUser,
        tableState,
        itemsTable,
        handleCreateNew,
        handleAction,
        confirmDelete,
        handleSuccess
    };
};